/**
 * WebView Data Mapping Utilities
 */

import { WebViewExternalProps } from '../types';
import { UIConfig } from '../../../types/whiteboardProps';
import { GetQbTestForEditOutput } from '../../../lib/api/types';

/**
 * Map WebView props to test data format
 */
export function mapWebViewPropsToTestData(props: WebViewExternalProps): Partial<GetQbTestForEditOutput> {
  // Handle both dersGrupListesi and coursesGroups
  const coursesGroups = (props as any).dersGrupListesi || props.coursesGroups;
  
  if (!props.testConfig && !props.questions && !coursesGroups) {
    return {};
  }

  const { testConfig, questions } = props;

  return {
    qbTest: testConfig ? {
      id: testConfig.testId,
      examType: testConfig.studyTask.params.examType,
      // Add other fields as needed
    } : undefined,
    questions: questions || [],
    coursesGroups: coursesGroups || [],
  } as Partial<GetQbTestForEditOutput>;
}

/**
 * Map WebView UI config to internal UI config
 */
export function mapWebViewUIConfig(webViewConfig?: UIConfig): UIConfig {
  if (!webViewConfig) {
    return getDefaultWebViewUIConfig();
  }

  // Merge with defaults to ensure all fields are present
  return {
    ...getDefaultWebViewUIConfig(),
    ...webViewConfig,
  };
}

/**
 * Get default UI config for WebView
 */
function getDefaultWebViewUIConfig(): UIConfig {
  return {
    showToolbar: true,
    showQuestionNav: true,
    showTimer: false,
    allowDrawing: true,
    allowEraser: true,
    allowShapes: true,
    allowHighlighter: true,
    readOnly: false,
    showOpticalForm: true,
    opticalAutoDetect: true,
    allowExport: false,
    animationsEnabled: true,
    hoverEffectsEnabled: true,
    selectionAnimationDuration: 200,
    voiceFeedbackEnabled: false,
    showOpticalAnswerSubmit: true,
    opticalAnswerAutoSave: true,
    opticalAnswerAutoSaveInterval: 30,
    allowOpticalAnswerClear: true,
    recordingPermissions: {
      canRecord: true,
      canViewRecordings: true,
      recordingVisibility: {
        showOwnRecordings: true,
        showTenantRecordings: true,
        showPublicRecordings: true,
      },
      recordingManagement: {
        canDeleteOwnRecordings: true,
        canExportOwnRecordings: false,
        canShareRecordings: true,
        canMakeRecordingsPublic: false,
      },
      recordingSettings: {
        quality: 'medium',
        autoSave: false,
        autoName: true,
      },
    },
    allowSolutionViewing: true,
    solutionVisibilityMode: 'own',
    allowOwnSolutionManagement: true,
    maxSolutionsToShow: 10,
    showSolutionAuthorInfo: false,
    showSolutionStats: false,
  };
}

/**
 * Map test params to exam type
 */
export function mapExamType(examType: number): 'deneme' | 'normal' {
  return examType === 1 ? 'deneme' : 'normal';
}

/**
 * Map WebView storage mode to internal storage mode
 */
export function mapStorageMode(mode?: string): 'firebase' | 'localStorage' {
  switch (mode) {
    case 'firebase':
      return 'firebase';
    case 'localStorage':
    case 'custom':
    default:
      return 'localStorage';
  }
}